import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetSalesQuery } from '../../../../entities/sales/sales.api';
import { selectUser } from '../../../../store/user/user.selector';
import { isListed, isSold } from '../../../../utils/sales/sales.constants';
import { calculateNetProfit } from '../../../../utils/sales/salesMath';

// styles
import {
    SalesDashboardContainer,
    StatsGrid,
    StatCard,
    StatCardLabel,
    StatCardValue,
    SectionHeader,
    SectionTitle,
    SectionCount,
    ListingsGrid,
    EmptyState,
    MessageState
} from './SalesDashboard.styles.js';

// components
import ListingCard from '../sales-tracker/components/listing-card/ListingCard.js';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
}).format(amount || 0);

export default function SalesDashboard() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  // Same argument shape as SalesTracker so both views share one cache entry
  const { data: sales = [], isLoading, error } = useGetSalesQuery(
    { uid: user?.uid },
    { skip: !user?.uid }
  );

  const { listed, stats } = useMemo(() => {
    const listedSales = sales.filter((sale) => isListed(sale.status));
    const soldSales = sales.filter((sale) => isSold(sale.status));

    return {
      listed: listedSales,
      stats: {
        activeListings: listedSales.length,
        listedValue: listedSales.reduce((sum, sale) => sum + (Number(sale.price) || 0), 0),
        cardsListed: listedSales.reduce((sum, sale) => sum + (Number(sale.cardCount) || 0), 0),
        // Derived rather than read from the stored field so sales saved under an
        // older profit formula still report a correct P/L. Only sold sales have
        // realized profit; a listed sale's figure is just its asking price.
        realizedProfit: soldSales.reduce((sum, sale) => sum + calculateNetProfit(sale), 0)
      }
    };
  }, [sales]);

  if (isLoading) {
    return (
      <SalesDashboardContainer>
        <MessageState>Loading sales...</MessageState>
      </SalesDashboardContainer>
    );
  }

  if (error) {
    return (
      <SalesDashboardContainer>
        <MessageState>Error loading sales: {error.message}</MessageState>
      </SalesDashboardContainer>
    );
  }

  return (
    <SalesDashboardContainer className='widget-bg'>
      <StatsGrid>
        <StatCard>
          <StatCardValue>{stats.activeListings}</StatCardValue>
          <StatCardLabel>Active Listings</StatCardLabel>
        </StatCard>
        <StatCard>
          <StatCardValue>{formatCurrency(stats.listedValue)}</StatCardValue>
          <StatCardLabel>Listed Value</StatCardLabel>
        </StatCard>
        <StatCard>
          <StatCardValue>{stats.cardsListed}</StatCardValue>
          <StatCardLabel>Cards Listed</StatCardLabel>
        </StatCard>
        <StatCard>
          <StatCardValue>{formatCurrency(stats.realizedProfit)}</StatCardValue>
          <StatCardLabel>Realized Profit</StatCardLabel>
        </StatCard>
      </StatsGrid>

      <SectionHeader>
        <SectionTitle>Active Listings</SectionTitle>
        <SectionCount>
          {stats.cardsListed} card{stats.cardsListed === 1 ? '' : 's'}
        </SectionCount>
      </SectionHeader>

      {listed.length === 0 ? (
        <EmptyState>
          <h2>No active listings</h2>
          <p>Mark cards as Listed in the Queue to see them here.</p>
        </EmptyState>
      ) : (
        <ListingsGrid>
          {listed.map((sale) => (
            <ListingCard
              key={sale.id}
              sale={sale}
              onClick={() => navigate(`/transactions/tracker/${sale.id}`)}
            />
          ))}
        </ListingsGrid>
      )}
    </SalesDashboardContainer>
  );
}
